import React, { useState, useEffect, useRef } from 'react';
import './dice-roller.css';

const DiceRoller = ({ socket, username, roomName }) => {
  const [rollHistory, setRollHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [target, setTarget] = useState('');
  const [comparison, setComparison] = useState('gte');
  const logRef = useRef(null);
  const [bonus, setBonus] = useState(0);
  const [numDice, setNumDice] = useState(1);
  const [targetMode, setTargetMode] = useState('sum'); // 'sum' or 'each'

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [rollHistory]);

  useEffect(() => {
    const spinWrappers = document.querySelectorAll('.spin-wrapper');
  
    spinWrappers.forEach(wrapper => {
      const randomAngle = Math.floor(Math.random() * 360);
      const randomSpeed = (10 + Math.random() * 10).toFixed(2); // 10s to 20s
      const randomDirection = Math.random() < 0.5 ? 'normal' : 'reverse';
  
      wrapper.style.transform = `rotate(${randomAngle}deg)`;
      wrapper.style.animation = `float-spin ${randomSpeed}s infinite linear ${randomDirection}`;
    });
  
    // Skull die will continue pulsing separately — we leave it untouched!
  }, []);
  
  const handleRoll = (sides) => {
  const bonusValue = parseInt(bonus || 0);
  const rolls = [];
  let totalRaw = 0;

  for (let i = 0; i < numDice; i++) {
    const raw = Math.floor(Math.random() * sides) + 1;
    rolls.push(raw);
    totalRaw += raw;
  }

  const finalTotal = totalRaw + bonusValue;
  setTotal(prev => prev + finalTotal);

  if (socket) {
    socket.emit('new-dice-roll', {
      username,
      sides,
      raw: rolls,
      bonus: bonusValue,
      result: finalTotal,
      room: roomName,
      target: target ? parseInt(target) : null,
      comparison,
      targetMode
    });
  }
};


  useEffect(() => {
    if (!socket) return;
  
    socket.on('new-dice-roll', ({ username, sides, raw, bonus, result, target, comparison, targetMode }) => {
  let message;

  if (sides === 'skull') {
    message = result === 1
      ? `💀 ${username} Skull Die: BOOM!`
      : `💀 ${username} Skull Die: ${result}`;
  } else {
    const rollList = Array.isArray(raw) ? raw.join(', ') : raw;
    const diceCount = Array.isArray(raw) ? raw.length : 1;
    const bonusPart = bonus ? `(+${bonus})` : '';
    const comparisonPart = target !== null ? ` vs ${comparison} ${target}` : '';
    const modePart = targetMode === 'each' ? ' (per die)' : '';

    message = `${username}: d${sides} x${diceCount} → [${rollList}] ${bonusPart} = ${result}${comparisonPart}${modePart}`;
  }

  // 🔁 Save full context into rollHistory
  setRollHistory(prev => [{
    message,
    raw,
    result,
    target,
    comparison,
    targetMode
  }, ...prev]);
});


  
    return () => {
      socket.off('new-dice-roll');
    };
  }, [socket]);
  
  const handleSkullRoll = () => {
    const result = Math.floor(Math.random() * 6) + 1;
    setTotal(prev => prev + result);
    socket.emit('new-dice-roll', { username, sides: 'skull', result, room: roomName });
  };
  
  const handleClear = () => {
    setTotal(0);
    setRollHistory([]);
  };

  return (
    <div className="dice-roller">
      <div className="dice-buttons">

  {/* d4 */}
  <button className="die" onClick={() => handleRoll(4)}>
    <div className="spin-wrapper">
      <img src="/images/d4frame.png" alt="d4 frame" className="die-frame" />
    </div>
  </button>

  {/* d6 */}
  <button className="die" onClick={() => handleRoll(6)}>
    <div className="spin-wrapper">
      <img src="/images/d6frame.png" alt="d6 frame" className="die-frame" />
    </div>
  </button>

  {/* d8 */}
  <button className="die" onClick={() => handleRoll(8)}>
    <div className="spin-wrapper">
      <img src="/images/d8frame.png" alt="d8 frame" className="die-frame" />
    </div>
  </button>

  {/* d10 */}
  <button className="die" onClick={() => handleRoll(10)}>
    <div className="spin-wrapper">
      <img src="/images/d10frame.png" alt="d10 frame" className="die-frame" />
    </div>
  </button>

  {/* d12 */}
  <button className="die" onClick={() => handleRoll(12)}>
    <div className="spin-wrapper">
      <img src="/images/d12frame.png" alt="d12 frame" className="die-frame" />
    </div>
  </button>

  {/* d20 */}
  <button className="die" onClick={() => handleRoll(20)}>
    <div className="spin-wrapper">
      <img src="/images/d20frame.png" alt="d20 frame" className="die-frame" />
    </div>
  </button>

  {/* d100 */}
  <button className="die" onClick={() => handleRoll(100)}>
    <div className="spin-wrapper">
      <img src="/images/d100frame.png" alt="d100 frame" className="die-frame" />
    </div>
  </button>

  <button className="die skull-icon" onClick={handleSkullRoll}>
    💀
  </button>

</div>
      <div className="controls">
        <label>
  Dice Count:
  <input
    type="number"
    min="1"
    max="20"
    value={numDice}
    onChange={(e) => setNumDice(parseInt(e.target.value) || 1)}
    placeholder="1"
  />
</label>

<label>
  Target Mode:
  <select
    value={targetMode}
    onChange={(e) => setTargetMode(e.target.value)}
  >
    <option value="sum">Total</option>
    <option value="each">Each Die</option>
  </select>
</label>

  <div className="control-row">
    <label>
      Target:
      <input
        type="number"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        placeholder="?"
      />
    </label>

    <label>
      <select
        value={comparison}
        onChange={(e) => setComparison(e.target.value)}
      >
        <option value="gte">≥</option>
        <option value="lte">≤</option>
      </select>
    </label>
  </div>

  <div className="control-row">
    <label>
      Bonus:
      <input
        type="number"
        value={bonus}
        onChange={(e) => setBonus(e.target.value)}
        placeholder="0"
      />
    </label>

    <button onClick={handleClear}>Clear Rolls</button>
  </div>
</div>


      <div id="results">
        <div id="log" ref={logRef}>
        {rollHistory.length > 0 ? (
  rollHistory.map((roll, index) => {
  const rollValue = roll.result;
  const numericTarget = parseInt(target);

  let isSuccess = false;

  if (roll.targetMode === 'each') {
    isSuccess = roll.raw.some(val =>
      roll.comparison === 'gte' ? val >= numericTarget :
      roll.comparison === 'lte' ? val <= numericTarget :
      false
    );
  } else {
    if (!isNaN(rollValue) && !isNaN(numericTarget)) {
      isSuccess = roll.comparison === 'gte'
        ? rollValue >= numericTarget
        : rollValue <= numericTarget;
    }
  }

  return (
    <div
      key={index}
      style={{ color: isSuccess ? '#4caf50' : '#f44336' }}
    >
      {roll.message}
    </div>
  );
})

) : (
  <div>No rolls yet.</div>
)}
          <div><strong>Total: {total}</strong></div>
          <div><strong>Successes: {
  rollHistory.reduce((totalSuccesses, roll) => {
    const numericTarget = parseInt(roll.target);

    if (roll.targetMode === 'each' && Array.isArray(roll.raw)) {
      const diceSuccesses = roll.raw.filter(val =>
        roll.comparison === 'gte' ? val >= numericTarget :
        roll.comparison === 'lte' ? val <= numericTarget :
        false
      ).length;

      return totalSuccesses + diceSuccesses;
    }

    // Sum mode (count whole roll if total succeeded)
    if (!isNaN(roll.result) && !isNaN(numericTarget)) {
      const success =
        roll.comparison === 'gte' ? roll.result >= numericTarget :
        roll.comparison === 'lte' ? roll.result <= numericTarget :
        false;
      return totalSuccesses + (success ? 1 : 0);
    }

    return totalSuccesses;
  }, 0)
}</strong></div>


        </div>
      </div>
    </div>
  );
};

export default DiceRoller;
